/**
 * Where a newly added item lands.
 *
 * Unity ProjectUIHelper.GetMouseCoords seeds the current touch or screen centre; the
 * final emulated Drag.GetIntersection keeps the whole item in the active room and before
 * blocking walls. A room polygon's centroid is NOT a usable fallback: for an L-shaped
 * room it lies in the notch, outside the walls, which is exactly where "+" used to drop
 * furniture.
 */
import type { Item, Level, Vec2 } from '../core/hp/types';
import { constrainItemToRoom, containsPoint } from './sceneGesture';

export interface PlacementSize { w: number; h: number; d: number }
export interface WalkPlacementContext { position: Vec2; forward: Vec2 }

export function placementPoint(level: Level, look: Vec2 | null, size: PlacementSize, rotation = 0, walk?: WalkPlacementContext): Vec2 {
  const rooms = level.rooms.filter(room => room.polygon.length >= 3);
  if (!rooms.length) return look ?? (walk ? { x: walk.position.x + walk.forward.x * 2, z: walk.position.z + walk.forward.z * 2 } : { x: 0, z: 0 });
  const largest = rooms.reduce((best, room) => (room.area > best.area ? room : best), rooms[0]);
  let candidate = look ?? interiorPoint(largest.polygon);
  // Unity completes catalog insertion through Drag.GetIntersection in the current ViewRoom.
  // A level floor ray alone can hit another room behind a wall, or miss entirely at the horizon.
  const cameraRoom = walk ? rooms.filter(room => containsPoint(room.polygon, walk.position)).sort((a, b) => a.area - b.area)[0]
    ?? nearestRoom(rooms, walk.position) : null;
  const room = cameraRoom ?? rooms.find(room => containsPoint(room.polygon, candidate)) ?? nearestRoom(rooms, candidate);
  if (walk) candidate = walkPoint(level, room.polygon, look, size, rotation, walk);
  const probe = (position: Vec2): Item => ({ id: '__placement__', modelRef: { kind: 'none', id: '', raw: '' }, placement: 'floor',
    position, elevation: 0, rotation, size, scale: [1, 1, 1], mirrored: false, pivot: 'bottom' });
  const fits = (position: Vec2) => footprintInside(probe(position), room.polygon);

  const direct = constrainItemToRoom(probe(candidate), room.polygon);
  if (fits(direct)) return direct;
  // Far outside: step in from the nearest wall by half the footprint, then settle.
  const edge = nearestEdgePoint(room.polygon, candidate);
  const inward = inwardNormal(room.polygon, edge);
  const reach = Math.hypot(size.w, size.d) / 2 + .05;
  const stepped = { x: edge.point.x + inward.x * reach, z: edge.point.z + inward.z * reach };
  const settled = constrainItemToRoom(probe(stepped), room.polygon);
  if (fits(settled)) return settled;
  const deep = constrainItemToRoom(probe(interiorPoint(room.polygon)), room.polygon);
  return fits(deep) ? deep : interiorPoint(room.polygon);
}

/** Cap the camera ray before the first room edge or solid wall, allowing for the whole item. */
function walkPoint(level: Level, polygon: Vec2[], look: Vec2 | null, size: PlacementSize, rotation: number, camera: WalkPlacementContext): Vec2 {
  const origin = containsPoint(polygon, camera.position) ? camera.position : interiorPoint(polygon);
  const forwardLength = Math.hypot(camera.forward.x, camera.forward.z) || 1;
  const forward = { x: camera.forward.x / forwardLength, z: camera.forward.z / forwardLength };
  const reach = Math.max(2, ...polygon.map(point => Math.hypot(point.x - origin.x, point.z - origin.z))) * 2;
  const aimed = look && (look.x - origin.x) * forward.x + (look.z - origin.z) * forward.z > 0
    ? look : { x: origin.x + forward.x * reach, z: origin.z + forward.z * reach };
  const length = Math.hypot(aimed.x - origin.x, aimed.z - origin.z);
  if (length < 1e-6) return origin;
  const direction = { x: (aimed.x - origin.x) / length, z: (aimed.z - origin.z) / length };
  const c = Math.cos(rotation), s = Math.sin(rotation);
  let distance = length;
  const cap = (a: Vec2, b: Vec2, thickness = 0, wallId?: string) => {
    const dx = b.x - a.x, dz = b.z - a.z, span = Math.hypot(dx, dz), cross = direction.x * dz - direction.z * dx;
    if (span < 1e-6 || Math.abs(cross) < 1e-9) return;
    const ox = a.x - origin.x, oz = a.z - origin.z;
    const t = (ox * dz - oz * dx) / cross, u = (ox * direction.z - oz * direction.x) / cross;
    if (t <= 1e-6) return;
    const nx = -dz / span, nz = dx / span;
    const halfNormal = Math.abs(nx * c + nz * s) * size.w / 2 + Math.abs(-nx * s + nz * c) * size.d / 2;
    const halfAlong = Math.abs(dx / span * c + dz / span * s) * size.w / 2 + Math.abs(-dx / span * s + dz / span * c) * size.d / 2;
    if (u * span < -halfAlong - .01 || u * span > span + halfAlong + .01) return;
    if (wallId && level.openings.some(opening => opening.wallId === wallId && opening.kind !== 'window'
      && !opening.modelRef.id.startsWith('wall.windows/') && opening.sill <= .01 && opening.height >= size.h
      && Math.abs(u * span - opening.offset) + halfAlong + .01 < opening.width / 2)) return;
    const clearance = (halfNormal + thickness / 2 + .01) / Math.abs(direction.x * nx + direction.z * nz);
    distance = Math.min(distance, Math.max(0, t - clearance));
  };
  for (let i = 0; i < polygon.length; i++) cap(polygon[i], polygon[(i + 1) % polygon.length]);
  for (const wall of level.walls) cap(wall.start, wall.end, wall.thickness, wall.id);
  return { x: origin.x + direction.x * distance, z: origin.z + direction.z * distance };
}

function footprintInside(item: Item, polygon: Vec2[]): boolean {
  const c = Math.cos(item.rotation), s = Math.sin(item.rotation), hw = item.size.w / 2, hd = item.size.d / 2;
  return [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].every(([u, v]) =>
    containsPoint(polygon, { x: item.position.x + u * c - v * s, z: item.position.z + u * s + v * c }));
}

/** A point well inside the polygon: the grid sample farthest from every edge. */
export function interiorPoint(polygon: Vec2[]): Vec2 {
  const xs = polygon.map(p => p.x), zs = polygon.map(p => p.z);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
  let best: Vec2 | null = null, bestDistance = -1;
  const steps = 28;
  for (let i = 1; i < steps; i++) for (let j = 1; j < steps; j++) {
    const p = { x: x0 + (x1 - x0) * i / steps, z: z0 + (z1 - z0) * j / steps };
    if (!containsPoint(polygon, p)) continue;
    const d = nearestEdgePoint(polygon, p).distance;
    if (d > bestDistance) { bestDistance = d; best = p; }
  }
  return best ?? polygon[0];
}

function nearestRoom(rooms: Level['rooms'], point: Vec2) {
  return rooms.reduce((best, room) => (nearestEdgePoint(room.polygon, point).distance < nearestEdgePoint(best.polygon, point).distance ? room : best), rooms[0]);
}

function nearestEdgePoint(polygon: Vec2[], p: Vec2): { point: Vec2; distance: number; edge: number } {
  let closest: Vec2 = polygon[0], distance = Infinity, edge = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length], dx = b.x - a.x, dz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / (dx * dx + dz * dz || 1)));
    const point = { x: a.x + dx * t, z: a.z + dz * t }, d = Math.hypot(point.x - p.x, point.z - p.z);
    if (d < distance) { closest = point; distance = d; edge = i; }
  }
  return { point: closest, distance, edge };
}

/** Unit normal of `edge` pointing into the polygon. */
function inwardNormal(polygon: Vec2[], at: { edge: number; point: Vec2 }): Vec2 {
  const a = polygon[at.edge], b = polygon[(at.edge + 1) % polygon.length];
  const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
  const n = { x: -(b.z - a.z) / len, z: (b.x - a.x) / len };
  const probe = { x: at.point.x + n.x * .01, z: at.point.z + n.z * .01 };
  return containsPoint(polygon, probe) ? n : { x: -n.x, z: -n.z };
}
